import { useState } from 'react';
import { SideNav } from '@/components/navigation/SideNav.tsx';
import { useAuth } from '@/lib/auth/AuthProvider.tsx';
import { Button } from '@/components/ui/button.tsx';
import { Menu, Plus, Settings, LogOut, FileText, Folder } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu.tsx';
import { NotificationBell } from '@/components/notifications/NotificationBell.tsx';
import { ReportFeedbackButton } from '@/components/feedback/ReportFeedbackButton.tsx';
import { YourSubmissionsBell } from '@/components/feedback/YourSubmissionsBell.tsx'; // R2: header entry + unseen badge
import { GlobalMessagesBell } from '@/components/projects/GlobalMessagesBell.tsx'; // R2: Global messages
import { TenantSwitcher } from '@/components/TenantSwitcher.tsx';
import { OfflineIndicator } from '@/components/ui/OfflineIndicator.tsx';
import { useOfflineSync } from '@/hooks/useOfflineSync.ts';
import { OnboardingWizard } from '@/components/onboarding/OnboardingWizard';
import { useOnboardingStatus } from '@/hooks/useOnboardingStatus';

interface DashboardLayoutProps {
  children: React.ReactNode;
  title?: string;
  actions?: React.ReactNode;
}

export function DashboardLayout({ children, title, actions }: DashboardLayoutProps) {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { syncState, isOnline } = useOfflineSync();
  const { data: onboardingData } = useOnboardingStatus();
  const showWizard = onboardingData?.onboarding_status === 'pending' ||
                     onboardingData?.onboarding_status === 'in_progress';

  return (
    <div className="flex h-screen bg-background">
      {/* Onboarding Wizard Overlay — Phase 10f */}
      {showWizard && <OnboardingWizard initialFirmType={onboardingData?.firm_type} />}

      {/* Side Navigation */}
      <SideNav mobileOpen={mobileMenuOpen} onMobileClose={() => setMobileMenuOpen(false)} />

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col overflow-hidden min-w-0">
        {/* Top Header */}
        <header className="h-16 border-b bg-background flex items-center justify-between px-4 lg:px-6">
          <div className="flex items-center gap-2">
            {/* Mobile hamburger menu */}
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden"
              onClick={() => setMobileMenuOpen(true)}
              aria-label="Open menu"
            >
              <Menu className="h-5 w-5" />
            </Button>
          </div>

          <div className="flex items-center gap-4">
            {/* Offline/Sync Status */}
            <OfflineIndicator isOnline={isOnline} syncState={syncState} />

            {/* Quick Create Menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button size="sm" className="gap-2">
                  <Plus className="h-4 w-4" />
                  New
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => navigate('/invoices/new')}>
                  <FileText className="mr-2 h-4 w-4" />
                  New Invoice
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/projects/new')}>
                  <Folder className="mr-2 h-4 w-4" />
                  New Project
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/projects')}>
                  <FileText className="mr-2 h-4 w-4" />
                  <div className="flex items-center gap-2">
                    <span>New Billable Item</span>
                    {/* TODO Phase 5: Implement standalone billable item creation with project selector */}
                    <span className="text-xs text-muted-foreground">(via Projects)</span>
                  </div>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Report Feedback — app-wide, every tenant/role (SPEC-stakeholder-portal R3) */}
            <ReportFeedbackButton />

            {/* "Your submissions" — icon-only, stakeholder_portal-capable users +
                super-admins (SPEC-stakeholder-portal-presentation-redesign R2).
                Replaces the retired Stakeholder Portal / Coordination left-nav pages. */}
            <YourSubmissionsBell />

            {/* R2: Global Messages Bell */}
            <GlobalMessagesBell />

            {/* Notifications */}
            <NotificationBell />

            {/* Settings */}
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/settings')}
              aria-label="Open settings"
            >
              <Settings className="h-4 w-4" />
            </Button>

            {/* Tenant Switcher (9.32) - only shows for multi-tenant users */}
            <TenantSwitcher />

            {/* User Menu */}
            <div className="flex items-center gap-3 pl-3 border-l">
              <div className="text-sm text-right hidden sm:block">
                <div className="font-medium">{user?.name || user?.email?.split('@')[0]}</div>
                <div className="text-xs text-muted-foreground">{user?.email}</div>
              </div>
              <Button variant="ghost" size="icon" onClick={signOut}>
                <LogOut className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </header>

        {/* Page Header — a subtle brand-tinted band (primary token, not a flat grey
            placeholder) with an accent rule, so every page reads as intentional. */}
        {(title || actions) && (
          <div className="border-b border-primary/10 bg-gradient-to-r from-primary/5 via-background to-background px-4 lg:px-6 py-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              {title && (
                <h1 className="text-2xl font-semibold tracking-tight min-w-0 truncate">{title}</h1>
              )}
              {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
            </div>
          </div>
        )}

        {/* Main Content */}
        <main className="flex-1 overflow-y-auto overflow-x-hidden p-4 lg:p-6">
          {children}
        </main>
      </div>
    </div>
  );
}